import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLookupsContext } from "hooks/useContext";
import useNotify from "hooks/useNotify";
import { actions } from "store/actions";
import profileApi from "api/profile";
import { scrollIntoView } from "utils/helper";

function usePersonalInformation({ countryId } = {}) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userDetails || {});
  const [isLoading, setIsLoading] = useState(false);

  const { countries, nationalities, cities, getCities } =
    useLookupsContext(true);

  const { notifyError, notifySuccess } = useNotify();

  const nationalitiesOptions = nationalities.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const countriesOptions = countries.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const citiesOptions = cities.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  // Fetch cities when country changes
  useEffect(() => {
    if (countryId) {
      getCities(countryId);
    }
  }, [countryId]);

  const onEditSave = async (values) => {
    try {
      setIsLoading(true);

      const {
        firstName,
        lastName,
        nationalities: nationalitiesIds,
        dateOfBirth,
        gender,
        phoneNumber,
        email,
        address,
        country: countryId,
        city: cityId,
      } = values;

      const payload = {
        firstName,
        lastName,
        nationalities: nationalitiesIds,
        dateOfBirth,
        gender,
        phone: phoneNumber,
        email,
        address,
        countryId,
        cityId,
      };

      await profileApi.updatePersonalDetailsApi(userDetails?.vendorId, payload);

      const updatedData = {
        ...userDetails,
        ...values,
        userNationalities: nationalities.filter((elem) =>
          values.nationalities.includes(elem.id)
        ),
        country: countries.find((elem) => elem.id === values.country),
        city: cities.find((elem) => elem.id === values.city),
      };
      dispatch(actions.setUserDetails(updatedData));

      notifySuccess(t("personalDetailsUpdated"));
      scrollIntoView("personalDetailsSection");
    } catch (err) {
      console.error(err);
      notifyError(err?.data?.error ?? err?.message ?? "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    onEditSave,
    isLoading,
    countries,
    nationalities,
    cities,
    nationalitiesOptions,
    countriesOptions,
    citiesOptions,
  };
}

export default usePersonalInformation;
